import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { reduxForm } from 'redux-form';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import ORTypography from 'components/ORTypography/ORTypography';
import SegmentClickTracker from 'components/SegmentClickTracker/SegmentClickTrackerContainer';
import ServiceSelector from './ServiceSelector/ServiceSelectorContainer';
import FormSelect from './FormSelect/FormSelectContainer';

const propTypes = {
  catalogServiceNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  availableServiceSubtypesNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedService: PropTypes.string,
  selectedProvider: PropTypes.string,
  orgIsAwsOnly: PropTypes.bool.isRequired,
  isPrivateRegion: PropTypes.bool.isRequired,
};

const useStyles = makeStyles((theme) => ({
  serviceSelectionSectionLabel: {
    marginBottom: theme.spacing(1),
    fontSize: '.8571rem',
    color: theme.palette.text.primary,
  },
  serviceSelectorsContainer: {
    display: 'flex',
    flexWrap: 'wrap',

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  serviceSelector: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),

    [theme.breakpoints.down('sm')]: {
      marginRight: 'unset',
      width: '100%',
    },
  },
  selectField: {
    marginTop: theme.spacing(1),
    width: '100%',
  },
  privateRegionText: {
    fontSize: '.8571rem',
    color: theme.customColors.red,
    paddingLeft: theme.spacing(1),
    display: 'block',
  },
  regionInfoIconContainer: {
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    marginTop: theme.spacing(1),
  },
  regionInfoIcon: {
    color: theme.customColors.mediumGrey,
  },
  regionInfoText: {
    marginLeft: theme.spacing(0.5),
    fontSize: '.8571rem',
    color: theme.palette.text.primary,
    fontStyle: 'italic',
  },
}));

const defaultProps = {
  selectedService: '',
  selectedProvider: '',
};

export function UnstyledStepOne({
  catalogServiceNames,
  availableServiceSubtypesNames,
  selectedService,
  selectedProvider,
  orgIsAwsOnly,
  isPrivateRegion,
}) {
  const classes = useStyles();

  const servicesAlsoInORv4 = orgIsAwsOnly ? [] : ['MongoDB', 'Redis', 'Elasticsearch'];
  const orv5ServiceSelectors = catalogServiceNames.map((serviceName) => (
    <SegmentClickTracker key={serviceName} buttonName={serviceName}>
      <ServiceSelector
        key={serviceName}
        serviceName={serviceName}
        className={classes.serviceSelector}
      />
    </SegmentClickTracker>
  ));

  const serviceSelectors = orv5ServiceSelectors;

  return (
    <form>
      <ORTypography className={classes.serviceSelectionSectionLabel}>
        Select your service:
      </ORTypography>

      <div className={classes.serviceSelectorsContainer}>{serviceSelectors}</div>

      <FormSelect
        label="Cloud Provider"
        name="cloudProvider"
        className={classes.selectField}
        isDisabled={Boolean(!selectedService)}
        isORv4={servicesAlsoInORv4.includes(selectedService)}
      />
      <FormSelect
        label="Region"
        name="region"
        className={classes.selectField}
        isDisabled={Boolean(!selectedProvider)}
      />
      {isPrivateRegion && (
        <span className={classes.privateRegionText}>
          * FYI: You have selected a private region for your instance.
        </span>
      )}
      <div className={classes.regionInfoIconContainer}>
        <InfoOutlinedIcon className={classes.regionInfoIcon} fontSize="small" />
        <ORTypography className={classes.regionInfoText}>
          {/* TODO The link below needs to point directly to the page about private regions */}
          <a href="https://docs.objectrocket.com" target="_blank" rel="noopener noreferrer">
            See our docs
          </a>
          &nbsp;for guidance on choosing a Private or Public Option.
        </ORTypography>
      </div>
      {availableServiceSubtypesNames.length > 1 && (
        <FormSelect label="Type" name="serviceSubtype" className={classes.selectField} />
      )}
    </form>
  );
}

UnstyledStepOne.propTypes = propTypes;
UnstyledStepOne.defaultProps = defaultProps;

export default reduxForm({
  form: 'create',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(UnstyledStepOne);
